import * as customerUIActionCreators from '@makemydeal/dr-dash-store/actions/customerUIActionCreators';
import * as customerUISelectors from '@makemydeal/dr-dash-store/selectors/customerUISelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomerDetails } from './CustomerDetails';
import { CustomerSearch } from './CustomerSearch';
import type { CustomerCardMessageContentSelector, CustomerCardShowOptions, CustomerSearchCallback } from './CustomerUIContext';
import { CustomerUIContext } from './CustomerUIContext';
import { CustomerSideBarTabEnum } from './CustomerSidebarProps';

/**
 * Stable New Relic event names emitted when the user opens the Common Consumer
 * Card or the customer search drawer. These provide a usage baseline for the
 * "MV — Common Consumer Card Integration Health" dashboard so failure rates
 * can be contextualized against actual user intent.
 */
export const CCC_CARD_OPENED_EVENT = 'MV:CCC:CardOpened';
export const CCC_SEARCH_OPENED_EVENT = 'MV:CCC:SearchOpened';

export type CustomerUIProviderProps = PropsWithChildren;

/**
 * Customer card provider manages slide out state for the customer details panel. We're using context here so the toggle function
 * is accessible to any part of the application (like the header)
 */
export const CustomerUIProvider: FC<CustomerUIProviderProps> = ({ children }) => {
    // NOTE: grouping state so they change in parallel downstream; i.e. show + option == 1 state change propagation
    const dispatch = useDispatch();
    const buyerCommonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
    const dealershipId = useSelector(offerReduxSelectors.getCommonOrgId);
    // Sourced for New Relic enrichment so MV:CCC:* events on the
    // "MV — Common Consumer Card Integration Health" dashboard can be sliced
    // / drilled-into per dealer (string-cast for stable NRQL FACET grouping).
    const dealerId = useSelector(dealerSelectors.getDealerId);

    // redux managed properties
    const customerCardShowing = useSelector(customerUISelectors.getCustomerCardShowing);
    const customerSearchShowing = useSelector(customerUISelectors.getCustomerSearchShowing);
    const customerCardOptions = useSelector(customerUISelectors.getCustomerCardOptions);

    // variable callback references that cannot be stored in state.
    const refCustomerSelectedCallback = useRef<CustomerSearchCallback | null>(null);
    const refCustomerChangedCallback = useRef<CustomerSearchCallback | null>(null);
    const refMessageContentSelector = useRef<CustomerCardMessageContentSelector | null>(null);

    // local delegates to manage the callbacks
    const localCustomerSelectedCallback = useCallback(
        (customer: CommonConsumer) => {
            if (refCustomerSelectedCallback.current) {
                refCustomerSelectedCallback.current(customer);
            }
        },
        [refCustomerSelectedCallback]
    );
    const localCustomerChangedCallback = useCallback(
        (customer: CommonConsumer) => {
            if (refCustomerChangedCallback.current) {
                refCustomerChangedCallback.current(customer);
            }
        },
        [refCustomerChangedCallback]
    );

    const localCustomerCardMessageContentSelector = useCallback(
        (state: any) => {
            if (refMessageContentSelector.current) {
                return refMessageContentSelector.current(state);
            }
            return '';
        },
        [refMessageContentSelector]
    );

    const showSearch = useCallback(
        (show: boolean, callback?: CustomerSearchCallback) => {
            if (callback) refCustomerSelectedCallback.current = callback;
            if (show) {
                logNewRelicPageAction(CCC_SEARCH_OPENED_EVENT, {
                    environment: offerSessionUtils.getEnvironmentType(),
                    dealerId: dealerId != null ? String(dealerId) : ''
                });
            }
            dispatch(customerUIActionCreators.showCustomerSearch(show));
        },
        [dispatch, dealerId]
    );

    const showCustomerCard = useCallback(
        (show: boolean, options: CustomerCardShowOptions = {}) => {
            const { messageContentSelector: _messageContentSelector, customerEditedCallback, ...stateOptions } = options;
            stateOptions.commonConsumerId = stateOptions.commonConsumerId ?? buyerCommonConsumerId;

            if (options.messageContentSelector) refMessageContentSelector.current = options.messageContentSelector;
            if (customerEditedCallback) refCustomerChangedCallback.current = customerEditedCallback;
            if (show) {
                logNewRelicPageAction(CCC_CARD_OPENED_EVENT, {
                    environment: offerSessionUtils.getEnvironmentType(),
                    tab: stateOptions.tab ?? 'information',
                    dealerId: dealerId != null ? String(dealerId) : ''
                });
            }
            dispatch(customerUIActionCreators.showCustomerCard(show, show ? stateOptions : undefined));
        },
        [dispatch, buyerCommonConsumerId, dealerId]
    );

    // internal toggle functions; these are used from within the customer Search/Details components to self-close
    const internalSetShowCustomerCard = (show: boolean) => {
        dispatch(customerUIActionCreators.showCustomerCard(show));
    };
    const internalSetShowCustomerSearch = (show: boolean) => {
        dispatch(customerUIActionCreators.showCustomerSearch(show));
    };

    return (
        <CustomerUIContext.Provider value={useMemo(() => ({ showCustomerCard, showSearch }), [showCustomerCard, showSearch])}>
            {children}
            <CustomerDetails
                show={customerCardShowing}
                setShow={internalSetShowCustomerCard}
                commonConsumerId={customerCardOptions.commonConsumerId ?? ''}
                onCustomerChanged={localCustomerChangedCallback}
                messageContentSelector={localCustomerCardMessageContentSelector}
                tab={(customerCardOptions.tab as CustomerSideBarTabEnum) ?? CustomerSideBarTabEnum.Information}
                waitForOfferSave={customerCardOptions.waitForOfferSave}
                enableReadOnlyEditFields={customerCardOptions.readOnlyFields}
                occDefaultTab={customerCardOptions.defaultTab}
            />
            <CustomerSearch
                show={customerSearchShowing}
                setShow={internalSetShowCustomerSearch}
                commonOrgId={dealershipId} // use "CoOrgTestId1" for local testing
                onCustomerSelected={localCustomerSelectedCallback}
            />
        </CustomerUIContext.Provider>
    );
};
